import React from 'react';
import { Zap, FileText, Sparkles, Globe } from 'lucide-react';

const OpenAiIcon = ({ className, size = 18 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
  </svg>
);

const MODELS = [
  { id: 'auto', label: 'Auto', cost: 'Cost optimized', icon: <Zap className="text-blue-500" size={16} />, color: 'blue' },
  { id: 'haiku', label: 'Haiku', cost: '~$0.10/doc', icon: <FileText className="text-green-500" size={16} />, color: 'green' },
  { id: 'sonnet', label: 'Sonnet', cost: '~$0.50/doc', icon: <Sparkles className="text-purple-500" size={16} />, color: 'purple' },
  { id: 'gpt-4.1', label: 'GPT-4.1', cost: '~$0.20/doc', icon: <OpenAiIcon className="text-emerald-500" size={16} />, color: 'emerald' },
  { id: 'gpt-5.2', label: 'GPT-5.2', cost: '~$1.00/doc', icon: <OpenAiIcon className="text-teal-500" size={16} />, color: 'teal' },
  { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', cost: '~$0.15/doc', icon: <OpenAiIcon className="text-cyan-500" size={16} />, color: 'cyan' },
];

const OPENROUTER_PREFIX = 'openrouter:';
const DEFAULT_OPENROUTER_MODEL = 'openrouter:anthropic/claude-haiku-4.5';

export default function AiModelPicker({ value, onChange, compact = false }) {
  const isOpenRouter = typeof value === 'string' && value.startsWith(OPENROUTER_PREFIX);
  const openRouterSlug = isOpenRouter ? value.slice(OPENROUTER_PREFIX.length) : '';
  return (
    <div>
      <div className={`flex ${compact ? 'gap-1.5 flex-wrap' : 'gap-3'}`}>
        {MODELS.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onChange(m.id)}
            className={`${compact ? 'px-2 py-1.5' : 'flex-1 p-3'} rounded-lg border-2 transition-colors ${
              value === m.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {!compact && (
              <div className="flex items-center justify-center mb-1">{m.icon}</div>
            )}
            <div className={`font-medium text-gray-900 ${compact ? 'text-[11px]' : 'text-sm'}`}>
              {compact && m.icon} {m.label}
            </div>
            <div className={`text-gray-500 ${compact ? 'text-[10px]' : 'text-xs'}`}>{m.cost}</div>
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(isOpenRouter ? value : DEFAULT_OPENROUTER_MODEL)}
          className={`${compact ? 'px-2 py-1.5' : 'flex-1 p-3'} rounded-lg border-2 transition-colors ${
            isOpenRouter
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          {!compact && (
            <div className="flex items-center justify-center mb-1">
              <Globe className="text-indigo-500" size={16} />
            </div>
          )}
          <div className={`font-medium text-gray-900 ${compact ? 'text-[11px]' : 'text-sm'}`}>
            {compact && <Globe className="text-indigo-500 inline" size={16} />} OpenRouter
          </div>
          <div className={`text-gray-500 ${compact ? 'text-[10px]' : 'text-xs'}`}>Any model</div>
        </button>
      </div>
      {isOpenRouter && (
        <input
          type="text"
          value={openRouterSlug}
          onChange={(e) => onChange(`${OPENROUTER_PREFIX}${e.target.value.trim()}`)}
          placeholder="anthropic/claude-haiku-4.5"
          className={`mt-2 w-full border border-gray-300 rounded-lg font-mono text-gray-700 ${
            compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-2 text-sm'
          }`}
        />
      )}
    </div>
  );
}
